import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BeginReconciliation from '../components/BeginReconciliation';
import { reconciliationService, ReconciliationData } from '../services/reconciliationService';
import toast from 'react-hot-toast';

const thColStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif", fontSize: 8, fontWeight: 500,
  letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c',
};

const rowStyle = (cleared: boolean): React.CSSProperties => ({
  display: 'grid', gap: 8, padding: '8px 16px',
  borderBottom: '1px solid rgba(241,245,249,1)',
  background: cleared ? 'rgba(90,201,140,0.05)' : 'transparent',
  fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5b5a52',
  cursor: 'pointer', transition: 'background 0.15s',
  alignItems: 'center',
});

const ReconciliationPage: React.FC = () => {
  const { bankAccountId } = useParams<{ bankAccountId: string }>();
  const navigate = useNavigate();

  const [showBeginModal, setShowBeginModal] = useState(true);
  const [reconciliationData, setReconciliationData] = useState<ReconciliationData | null>(null);
  const [clearedPayments, setClearedPayments] = useState<Set<string>>(new Set());
  const [clearedDeposits, setClearedDeposits] = useState<Set<string>>(new Set());
  const [summary, setSummary] = useState({ clearedBalance: 0, difference: 0 });
  const [loading, setLoading] = useState(false);

  const handleReconciliationStarted = (data: ReconciliationData) => {
    setReconciliationData(data);
    setShowBeginModal(false);
  };

  const togglePayment = (id: string) => {
    const newSet = new Set(clearedPayments);
    if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    setClearedPayments(newSet);
    updateSummary(newSet, clearedDeposits);
  };

  const toggleDeposit = (id: string) => {
    const newSet = new Set(clearedDeposits);
    if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    setClearedDeposits(newSet);
    updateSummary(clearedPayments, newSet);
  };

  const updateSummary = async (payments: Set<string>, deposits: Set<string>) => {
    if (!reconciliationData) return;
    try {
      const result = await reconciliationService.updateClearedTransactions(
        reconciliationData.reconciliation.id,
        { clearedPayments: Array.from(payments), clearedTransactions: Array.from(deposits) }
      );
      setSummary(result);
    } catch (error) {
      console.error('Error updating summary:', error);
    }
  };

  const handleReconcileNow = async () => {
    if (!reconciliationData) return;
    if (Math.abs(summary.difference) > 0.01) {
      toast.error(`Cannot reconcile with a difference of $${summary.difference.toFixed(2)}`);
      return;
    }
    setLoading(true);
    try {
      await reconciliationService.completeReconciliation(reconciliationData.reconciliation.id);
      toast.success('Reconciliation completed successfully!');
      navigate(`/bank-accounts/${bankAccountId}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to complete reconciliation');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!reconciliationData) { navigate(`/bank-accounts/${bankAccountId}`); return; }
    if (window.confirm('Are you sure you want to cancel this reconciliation?')) {
      try {
        await reconciliationService.cancelReconciliation(reconciliationData.reconciliation.id);
        toast.success('Reconciliation cancelled');
        navigate(`/bank-accounts/${bankAccountId}`);
      } catch {
        toast.error('Failed to cancel reconciliation');
      }
    }
  };

  const markAll = (type: 'payments' | 'deposits') => {
    if (type === 'payments' && reconciliationData) {
      const allIds = new Set(reconciliationData.checksAndPayments.map(t => t.id));
      setClearedPayments(allIds);
      updateSummary(allIds, clearedDeposits);
    } else if (type === 'deposits' && reconciliationData) {
      const allIds = new Set(reconciliationData.depositsAndCredits.map(t => t.id));
      setClearedDeposits(allIds);
      updateSummary(clearedPayments, allIds);
    }
  };

  const unmarkAll = (type: 'payments' | 'deposits') => {
    if (type === 'payments') {
      setClearedPayments(new Set());
      updateSummary(new Set(), clearedDeposits);
    } else {
      setClearedDeposits(new Set());
      updateSummary(clearedPayments, new Set());
    }
  };

  if (!bankAccountId) {
    return (
      <div style={{ padding: 24, fontFamily: "'Inter', sans-serif", color: '#c95a5a' }}>
        Invalid bank account
      </div>
    );
  }

  if (!reconciliationData) {
    return (
      <BeginReconciliation
        isOpen={showBeginModal}
        onClose={() => navigate(`/bank-accounts/${bankAccountId}`)}
        bankAccountId={bankAccountId}
        onReconciliationStarted={handleReconciliationStarted}
      />
    );
  }

  const { reconciliation, checksAndPayments, depositsAndCredits } = reconciliationData;
  const clearedPaymentsCount = clearedPayments.size;
  const clearedDepositsCount = clearedDeposits.size;
  const clearedPaymentsTotal = checksAndPayments
    .filter(t => clearedPayments.has(t.id))
    .reduce((sum, t) => sum + t.amount, 0);
  const clearedDepositsTotal = depositsAndCredits
    .filter(t => clearedDeposits.has(t.id))
    .reduce((sum, t) => sum + t.amount, 0);

  const isBalanced = Math.abs(summary.difference) < 0.01;

  const ghostBtn: React.CSSProperties = {
    padding: '5px 14px', fontFamily: "'Inter', sans-serif", fontSize: 8,
    letterSpacing: '0.15em', textTransform: 'uppercase',
    background: 'rgba(239,232,213,0.5)', color: '#6b6a5c',
    border: '1px solid rgba(92,78,51,0.22)', borderRadius: 3, cursor: 'pointer',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f4efe2', display: 'flex', flexDirection: 'column' }}>
      {/* Header bar */}
      <div style={{ background: '#ffffff', borderBottom: '1px solid rgba(92,78,51,0.2)', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>
            Bank Reconciliation
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
            Statement date: {new Date(reconciliation.statementDate).toLocaleDateString()}
          </div>
        </div>
        <button
          onClick={handleCancel}
          style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          ← Leave
        </button>
      </div>

      {/* Main content */}
      <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Two-column transaction tables */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          {/* Checks & Payments */}
          <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 300, color: '#1c2033' }}>Checks and Payments</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={ghostBtn} onClick={() => markAll('payments')}>Mark All</button>
                <button style={ghostBtn} onClick={() => unmarkAll('payments')}>Unmark All</button>
              </div>
            </div>
            {/* Column headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 80px 80px', gap: 8, padding: '8px 16px', background: 'rgba(201,165,90,0.02)', borderBottom: '1px solid rgba(239,232,213,0.6)' }}>
              <div />
              <div style={thColStyle}>Date / Payee</div>
              <div style={thColStyle}>CHK #</div>
              <div style={{ ...thColStyle, textAlign: 'right' }}>Amount</div>
            </div>
            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
              {checksAndPayments.length === 0 ? (
                <div style={{ padding: '32px 16px', textAlign: 'center', fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778' }}>
                  No checks or payments found
                </div>
              ) : checksAndPayments.map((t) => (
                <div
                  key={t.id}
                  style={{ ...rowStyle(clearedPayments.has(t.id)), gridTemplateColumns: '24px 1fr 80px 80px' }}
                  onClick={() => togglePayment(t.id)}
                  onMouseEnter={e => { if (!clearedPayments.has(t.id)) (e.currentTarget as HTMLDivElement).style.background = 'rgba(248,250,252,1)'; }}
                  onMouseLeave={e => { if (!clearedPayments.has(t.id)) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                >
                  <input
                    type="checkbox"
                    checked={clearedPayments.has(t.id)}
                    onChange={() => togglePayment(t.id)}
                    style={{ accentColor: '#d97706' }}
                    onClick={e => e.stopPropagation()}
                  />
                  <div>
                    <div style={{ color: '#5b5a52' }}>{new Date(t.date).toLocaleDateString()}</div>
                    <div style={{ fontSize: 10, color: '#6b6a5c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.payee}</div>
                  </div>
                  <div style={{ color: '#6b6a5c' }}>{t.checkNumber || '—'}</div>
                  <div style={{ textAlign: 'right', fontFamily: "'Inter', sans-serif", fontSize: 14, color: '#c95a5a' }}>
                    {t.amount.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '8px 16px', borderTop: '1px solid rgba(239,232,213,0.6)', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
              {clearedPaymentsCount} cleared · Total: <span style={{ color: '#c95a5a', fontFamily: "'Inter', sans-serif", fontSize: 14 }}>{clearedPaymentsTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Deposits & Credits */}
          <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 300, color: '#1c2033' }}>Deposits and Other Credits</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={ghostBtn} onClick={() => markAll('deposits')}>Mark All</button>
                <button style={ghostBtn} onClick={() => unmarkAll('deposits')}>Unmark All</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 60px 60px 80px', gap: 8, padding: '8px 16px', background: 'rgba(201,165,90,0.02)', borderBottom: '1px solid rgba(239,232,213,0.6)' }}>
              <div />
              <div style={thColStyle}>Date / Memo</div>
              <div style={thColStyle}>CHK #</div>
              <div style={thColStyle}>Type</div>
              <div style={{ ...thColStyle, textAlign: 'right' }}>Amount</div>
            </div>
            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
              {depositsAndCredits.length === 0 ? (
                <div style={{ padding: '32px 16px', textAlign: 'center', fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778' }}>
                  No deposits or credits found
                </div>
              ) : depositsAndCredits.map((t) => (
                <div
                  key={t.id}
                  style={{ ...rowStyle(clearedDeposits.has(t.id)), gridTemplateColumns: '24px 1fr 60px 60px 80px' }}
                  onClick={() => toggleDeposit(t.id)}
                  onMouseEnter={e => { if (!clearedDeposits.has(t.id)) (e.currentTarget as HTMLDivElement).style.background = 'rgba(248,250,252,1)'; }}
                  onMouseLeave={e => { if (!clearedDeposits.has(t.id)) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                >
                  <input
                    type="checkbox"
                    checked={clearedDeposits.has(t.id)}
                    onChange={() => toggleDeposit(t.id)}
                    style={{ accentColor: '#d97706' }}
                    onClick={e => e.stopPropagation()}
                  />
                  <div>
                    <div style={{ color: '#5b5a52' }}>{new Date(t.date).toLocaleDateString()}</div>
                    <div style={{ fontSize: 10, color: '#6b6a5c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.memo}</div>
                  </div>
                  <div style={{ color: '#6b6a5c' }}>{t.checkNumber || '—'}</div>
                  <div style={{ color: '#6b6a5c', fontSize: 10 }}>{t.type}</div>
                  <div style={{ textAlign: 'right', fontFamily: "'Inter', sans-serif", fontSize: 14, color: '#5ac98c' }}>
                    {t.amount.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '8px 16px', borderTop: '1px solid rgba(239,232,213,0.6)', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
              {clearedDepositsCount} cleared · Total: <span style={{ color: '#5ac98c', fontFamily: "'Inter', sans-serif", fontSize: 14 }}>{clearedDepositsTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Summary panel */}
        <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Left: cleared counts */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 4 }}>
                Cleared Items
              </div>
              {[
                { label: 'Checks and Payments', count: clearedPaymentsCount },
                { label: 'Deposits and Other Credits', count: clearedDepositsCount },
              ].map(({ label, count }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Inter', sans-serif", fontSize: 12 }}>
                  <span style={{ color: '#6b6a5c' }}>{label}</span>
                  <span style={{ color: '#1c2033', fontWeight: 500 }}>{count}</span>
                </div>
              ))}
            </div>

            {/* Right: balance summary */}
            <div style={{ borderLeft: '1px solid rgba(92,78,51,0.15)', paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { label: 'Beginning Balance', value: reconciliation.beginningBalance, dim: true },
                { label: `${clearedPaymentsCount} Checks and Payments`, value: -clearedPaymentsTotal, indent: true },
                { label: `${clearedDepositsCount} Deposits and Other Credits`, value: clearedDepositsTotal, indent: true },
                ...(reconciliation.serviceCharge > 0 ? [{ label: 'Service Charge', value: -reconciliation.serviceCharge }] : []),
                ...(reconciliation.interestEarned > 0 ? [{ label: 'Interest Earned', value: reconciliation.interestEarned }] : []),
              ].map(({ label, value, dim, indent }: any) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Inter', sans-serif", fontSize: 11, paddingLeft: indent ? 12 : 0 }}>
                  <span style={{ color: dim ? '#8a8778' : '#5b5a52' }}>{label}</span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: value < 0 ? '#c95a5a' : '#5b5a52' }}>{value.toFixed(2)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid rgba(92,78,51,0.15)', fontFamily: "'Inter', sans-serif", fontSize: 11 }}>
                <span style={{ color: '#5b5a52' }}>Ending Balance</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: '#5b5a52' }}>{reconciliation.endingBalance.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Inter', sans-serif", fontSize: 11 }}>
                <span style={{ color: '#5b5a52' }}>Cleared Balance</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: '#5b5a52' }}>{summary.clearedBalance.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: '1px solid rgba(92,78,51,0.22)', fontFamily: "'Inter', sans-serif", fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                <span style={{ color: isBalanced ? '#5ac98c' : '#c95a5a' }}>Difference</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, color: isBalanced ? '#5ac98c' : '#c95a5a' }}>
                  {summary.difference.toFixed(2)}
                </span>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
                <button
                  onClick={handleCancel}
                  style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0' }}
                >
                  Leave
                </button>
                <button
                  onClick={handleReconcileNow}
                  disabled={loading || !isBalanced}
                  style={{
                    padding: '10px 24px', fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500,
                    letterSpacing: '0.2em', textTransform: 'uppercase',
                    background: isBalanced ? 'linear-gradient(135deg, #d97706, #b45309)' : 'rgba(92,78,51,0.25)',
                    color: isBalanced ? '#1c2033' : '#c9c2ab',
                    border: 'none', borderRadius: 4,
                    cursor: loading || !isBalanced ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? 'Processing...' : 'Reconcile Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationPage;
